// src/pages/Home/Home.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Box,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
  Fade,
} from "@mui/material";
import {
  DeveloperBoard,
  Code,
  Cloud,
  Settings,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Phone,
  Email,
  LocationOn,
} from "@mui/icons-material";
import styles from "./Home.module.css";
import ClientsSlider from "../../components/common/ClientsSlider";
import Testimonials from "../../components/common/Testimonials";
import RequestCallback from "../../components/common/RequestCallback";

// Slider images (replace with your actual image URLs)
const sliderImages = [
  "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&h=600&fit=crop", // Technology/Code
  "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=600&fit=crop", // Digital/Network
  "https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=600&fit=crop", // Team/Office
];

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const slides = [
    {
      id: 1,
      title:
        "Do you want to stay ahead of global competition with your mobile apps?",
      description:
        "We develop bespoke forward-looking mobile apps for organisations in the education, medical, transportation and financial institutions to remain relevant in the global technology market.",
      image: sliderImages[0],
      cta: "Contact Us Now",
    },
    {
      id: 2,
      title:
        "Are you a business owner ready to take your business to the next level?",
      description:
        "We design and develop cutting edge dynamic websites. Our IT consultancy services will ensure business growth that stays at the forefront of emerging trends.",
      image: sliderImages[1],
      cta: "Get Started",
    },
    {
      id: 3,
      title: "Transform Your Digital Presence Today",
      description:
        "From concept to deployment, we provide end-to-end solutions that drive innovation and growth for your business.",
      image: sliderImages[2],
      cta: "Let's Talk",
    },
  ];

  const services = [
    {
      icon: <DeveloperBoard sx={{ fontSize: 36 }} />,
      title: "Hardware Engineering",
      desc: "PCB design, FPGA, signal integrity, and prototyping for industrial & medical.",
    },
    {
      icon: <Code sx={{ fontSize: 36 }} />,
      title: "Software Development",
      desc: "Web, mobile, desktop & embedded software. React, Node, Python, C/C++.",
    },
    {
      icon: <Cloud sx={{ fontSize: 36 }} />,
      title: "Cloud & IoT",
      desc: "AWS, Azure, IoT core, edge computing, data pipelines and analytics.",
    },
    {
      icon: <Settings sx={{ fontSize: 36 }} />,
      title: "Consultancy",
      desc: "Technical advisory, architecture reviews, team augmentation & project rescue.",
    },
  ];

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      handleNextSlide();
    }, 6000);
    return () => clearInterval(timer);
  }, [currentSlide]);

  const handlePrevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const quickServices = [
    "Hardware design",
    "Firmware & embedded",
    "Full-stack software",
    "Cloud & DevOps",
  ];

  return (
    <Box>
      {/* SLIDER SECTION */}
      <Box className={styles.sliderSection}>
        <Box className={styles.sliderContainer}>
          {slides.map((slide, index) => (
            <Fade
              key={slide.id}
              in={index === currentSlide}
              timeout={600}
              mountOnEnter
              unmountOnExit
            >
              <Box
                className={styles.slide}
                style={{
                  backgroundImage: `linear-gradient(135deg, rgba(11, 26, 46, 0.85) 0%, rgba(11, 26, 46, 0.4) 60%, rgba(11, 26, 46, 0.2) 100%), url(${slide.image})`,
                }}
              >
                <Container maxWidth="lg" className={styles.slideContent}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} lg={7}>
                      <Box className={styles.slideTextWrapper}>
                        <Typography
                          variant="h1"
                          className={styles.slideTitle}
                          sx={{
                            fontSize: isMobile
                              ? "2rem"
                              : isTablet
                                ? "2.8rem"
                                : "3.5rem",
                          }}
                        >
                          {slide.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          className={styles.slideDescription}
                          sx={{
                            fontSize: isMobile ? "0.95rem" : "1.1rem",
                          }}
                        >
                          {slide.description}
                        </Typography>
                        <Box className={styles.slideButtons}>
                          <Button
                            variant="contained"
                            className={styles.primaryButton}
                            startIcon={<Phone />}
                            size={isMobile ? "medium" : "large"}
                          >
                            {slide.cta}
                          </Button>
                          <Button
                            variant="outlined"
                            className={styles.secondaryButton}
                            startIcon={<ArrowForward />}
                            size={isMobile ? "medium" : "large"}
                          >
                            Learn More
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            </Fade>
          ))}

          {/* Slider Controls */}
          <Box className={styles.sliderControls}>
            <IconButton
              className={styles.sliderArrow}
              onClick={handlePrevSlide}
              aria-label="Previous slide"
            >
              <ArrowBack />
            </IconButton>
            <Box className={styles.sliderDots}>
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.sliderDot} ${
                    index === currentSlide ? styles.sliderDotActive : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </Box>
            <IconButton
              className={styles.sliderArrow}
              onClick={handleNextSlide}
              aria-label="Next slide"
            >
              <ArrowForward />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* ===== WASTE NO MORE TIME SECTION ===== */}
      <Box className={styles.wasteSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left Side - Text Content */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.wasteContent}>
                <Typography
                  variant="h2"
                  className={styles.wasteTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3.2rem" },
                  }}
                >
                  Waste No More Time
                </Typography>
                <Typography
                  variant="h5"
                  className={styles.wasteSubtitle}
                  sx={{
                    fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.4rem" },
                  }}
                >
                  Need a quality software for your business and organisation?
                </Typography>

                {/* Features in Single Row */}
                <Box className={styles.wasteFeaturesRow}>
                  <Box className={styles.wasteFeature}>
                    <Box className={styles.wasteFeatureIcon}>✓</Box>
                    <Typography variant="body1">Fast Delivery</Typography>
                  </Box>
                  <Box className={styles.wasteFeature}>
                    <Box className={styles.wasteFeatureIcon}>✓</Box>
                    <Typography variant="body1">Quality Assured</Typography>
                  </Box>
                  <Box className={styles.wasteFeature}>
                    <Box className={styles.wasteFeatureIcon}>✓</Box>
                    <Typography variant="body1">24/7 Support</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Button */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.wasteButtonWrapper}>
                <Button
                  variant="contained"
                  className={styles.wastePrimaryBtn}
                  size="large"
                >
                  Get Started Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== CLIENTS SLIDER ===== */}
      <ClientsSlider />

      {/* ===== HOW WE STARTED SECTION ===== */}
      <Box className={styles.startedSection}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Side - Image/Visual */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.startedImageWrapper}>
                <Box className={styles.startedImage}>
                  <Box className={styles.imageOverlay}>
                    <Box className={styles.experienceBadge}>
                      <Typography variant="h3" fontWeight={700}>
                        10+
                      </Typography>
                      <Typography variant="caption">
                        Years of Excellence
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Floating Stats Cards */}
                <Box className={styles.floatingStat1}>
                  <Typography variant="h4" fontWeight={700} color="#8cc61d">
                    50+
                  </Typography>
                  <Typography variant="caption">IT Professionals</Typography>
                </Box>
                <Box className={styles.floatingStat2}>
                  <Typography variant="h4" fontWeight={700} color="#8cc61d">
                    100%
                  </Typography>
                  <Typography variant="caption">Client Satisfaction</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Content */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.startedContent}>
                <Typography
                  variant="h2"
                  className={styles.startedTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  }}
                >
                  HOW WE <span className={styles.highlight}>STARTED</span>
                </Typography>

                <Box className={styles.startedText}>
                  <Typography
                    variant="body1"
                    className={styles.startedParagraph}
                  >
                    <strong>Wavelength Consultancy Solutions (WCS)</strong>, is
                    a UK-headquartered provider of IT consulting services and
                    bespoke software development with over{" "}
                    <strong>50 IT professionals</strong> located
                    internationally. Its subsidiary, <strong>Wavepalm</strong>,
                    focuses on the development of hardware and software
                    solutions.
                  </Typography>

                  <Typography
                    variant="body1"
                    className={styles.startedParagraph}
                  >
                    For over <strong>10 years</strong>, we have been
                    manufacturing high tech products, developing world class
                    mobile apps, training young minds to be experts in the field
                    of technology and providing business advisory services.
                  </Typography>

                  <Typography
                    variant="body1"
                    className={styles.startedParagraph}
                  >
                    As an Engineering and Software company, we pride ourselves
                    on the high level quality and efficiency of our services and
                    products – proven by our extensive customer base.
                  </Typography>

                  <Typography
                    variant="body1"
                    className={styles.startedParagraph}
                  >
                    Our current and potential clients in the global market are
                    from a range of sectors such as the{" "}
                    <strong>
                      education, medical, entertainment, media, transportation,
                      leisure
                    </strong>{" "}
                    and many more sectors.
                  </Typography>

                  <Typography
                    variant="body1"
                    className={styles.startedParagraph}
                  >
                    We have grown as a socially conscious organization with a
                    culture of giving back to our communities by undertaking
                    charity projects in the UK and across Africa.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  className={styles.startedButton}
                  endIcon={<ArrowForward />}
                >
                  Read more about us
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== OUR SERVICES SECTION ===== */}
      <Box className={styles.servicesSection}>
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box className={styles.servicesHeader}>
            <Typography
              variant="h2"
              className={styles.servicesTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              Our <span className={styles.highlight}>Services</span>
            </Typography>
            <Typography variant="body1" className={styles.servicesSubtitle}>
              Comprehensive solutions tailored to your business needs
            </Typography>
          </Box>

          {/* Services Cards */}
          <Grid container spacing={4}>
            {/* Card 1 - Hardware Manufacturing */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card className={styles.serviceCard}>
                <CardContent>
                  <Box className={styles.cardIconWrapper}>
                    <Box className={styles.cardIcon}>
                      <DeveloperBoard sx={{ fontSize: 40 }} />
                    </Box>
                  </Box>
                  <Typography variant="h5" className={styles.cardTitle}>
                    Hardware Manufacturing
                  </Typography>
                  <Typography
                    variant="body2"
                    className={styles.cardDescription}
                  >
                    We manufacture bespoke electronics, devices and gadgets such
                    as tablets, health trackers, phones, powerbanks and wi-fi
                    transmitters.
                  </Typography>
                  <Box className={styles.cardBrands}>
                    <Chip
                      label="Wavepalm Tablets"
                      size="small"
                      className={styles.brandChip}
                    />
                    <Chip
                      label="Wavefone"
                      size="small"
                      className={styles.brandChip}
                    />
                    <Chip
                      label="Wavesolar"
                      size="small"
                      className={styles.brandChip}
                    />
                    <Chip
                      label="Wavestream"
                      size="small"
                      className={styles.brandChip}
                    />
                    <Chip
                      label="Wavesmart"
                      size="small"
                      className={styles.brandChip}
                    />
                    <Chip
                      label="Wave2health"
                      size="small"
                      className={styles.brandChip}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    className={styles.cardButton}
                    endIcon={<ArrowForward />}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 2 - ICT Consultancy */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card className={styles.serviceCard}>
                <CardContent>
                  <Box className={styles.cardIconWrapper}>
                    <Box className={styles.cardIcon}>
                      <Settings sx={{ fontSize: 40 }} />
                    </Box>
                  </Box>
                  <Typography variant="h5" className={styles.cardTitle}>
                    ICT Consultancy
                  </Typography>
                  <Typography
                    variant="body2"
                    className={styles.cardDescription}
                  >
                    Our consulting include: IT solutions & services; customer
                    relation management services; client relationship
                    administration; human capital administration; and support
                    services.
                  </Typography>
                  <Box className={styles.cardFeatures}>
                    <Box className={styles.featureItem}>
                      <CheckCircle className={styles.featureCheck} />
                      <Typography variant="caption">
                        IT Solutions & Services
                      </Typography>
                    </Box>
                    <Box className={styles.featureItem}>
                      <CheckCircle className={styles.featureCheck} />
                      <Typography variant="caption">CRM Services</Typography>
                    </Box>
                    <Box className={styles.featureItem}>
                      <CheckCircle className={styles.featureCheck} />
                      <Typography variant="caption">
                        Business Analytics
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    className={styles.cardButton}
                    endIcon={<ArrowForward />}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 3 - Software Development */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card className={styles.serviceCard}>
                <CardContent>
                  <Box className={styles.cardIconWrapper}>
                    <Box className={styles.cardIcon}>
                      <Code sx={{ fontSize: 40 }} />
                    </Box>
                  </Box>
                  <Typography variant="h5" className={styles.cardTitle}>
                    Software Development
                  </Typography>
                  <Typography
                    variant="body2"
                    className={styles.cardDescription}
                  >
                    Our teams of developers are experienced professionals
                    proficient in native and hybrid mobile app and web
                    development using the latest cutting edge technology.
                  </Typography>
                  <Box className={styles.cardTech}>
                    <Chip
                      label="React"
                      size="small"
                      className={styles.techChip}
                    />
                    <Chip
                      label="React Native"
                      size="small"
                      className={styles.techChip}
                    />
                    <Chip
                      label="Node.js"
                      size="small"
                      className={styles.techChip}
                    />
                    <Chip
                      label="Python"
                      size="small"
                      className={styles.techChip}
                    />
                    <Chip
                      label="Flutter"
                      size="small"
                      className={styles.techChip}
                    />
                    <Chip
                      label="AWS"
                      size="small"
                      className={styles.techChip}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    className={styles.cardButton}
                    endIcon={<ArrowForward />}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== TESTIMONIALS ===== */}
      <Testimonials />

      {/* ===== REQUEST CALLBACK ===== */}
      <RequestCallback />
    </Box>
  );
};

export default Home;
